import { useState, useEffect } from 'react'
import { BASE } from '../utils/api'

interface Props {
  onLogin: () => void
}

type Mode = 'login' | 'register' | 'forgot'

const isLocalDev = ['localhost', '127.0.0.1'].includes(window.location.hostname)

async function apiPost(path: string, body: Record<string, unknown>, attempt = 0): Promise<{ status: number; ok: boolean; data: Record<string, unknown> }> {
  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    if (attempt < 2) {
      await new Promise(r => setTimeout(r, 1000 * (attempt + 1)))
      return apiPost(path, body, attempt + 1)
    }
    throw new Error(isLocalDev
      ? 'Нет соединения с сервером. Убедитесь, что API запущен (pnpm dev).'
      : 'Нет соединения с сервером. Попробуйте позже.')
  }
  const ct = res.headers.get('content-type') ?? ''
  if (!ct.includes('application/json') && !ct.includes('application/problem')) {
    if (attempt < 2 && res.status >= 500) {
      await new Promise(r => setTimeout(r, 1000 * (attempt + 1)))
      return apiPost(path, body, attempt + 1)
    }
    if (res.status === 404) {
      throw new Error(isLocalDev
        ? 'API не найден (404). Проверьте: запущен ли сервер и правильно ли настроен VITE_API_URL.'
        : 'Сервис временно недоступен. Попробуйте позже.')
    }
    throw new Error(isLocalDev
      ? `Сервер вернул неожиданный ответ (HTTP ${res.status}). Перезапустите сервер.`
      : 'Неожиданный ответ сервера. Попробуйте позже.')
  }
  const data = await res.json() as Record<string, unknown>
  if (res.status === 503 && attempt < 2) {
    await new Promise(r => setTimeout(r, 1000 * (attempt + 1)))
    return apiPost(path, body, attempt + 1)
  }
  return { status: res.status, ok: res.ok, data }
}

async function callLogin(email: string, password: string): Promise<Record<string, unknown>> {
  const { ok, status, data } = await apiPost('/auth/login', { email, password })
  if (!ok) {
    if (status === 429) throw new Error('Слишком много попыток входа. Подождите 1 минуту.')
    const msg = (data.message as string | undefined) ?? 'Ошибка сервера'
    throw new Error(Array.isArray(msg) ? (msg as string[]).join('. ') : msg)
  }
  if (!data.accessToken) throw new Error('Ответ сервера не содержит токен. Попробуйте ещё раз.')
  return data
}

async function callRegister(body: Record<string, unknown>): Promise<{ needsVerification: boolean; email: string } | Record<string, unknown>> {
  const { ok, status, data } = await apiPost('/auth/register', body)
  if (!ok) {
    if (status === 429) throw new Error('Слишком много попыток регистрации. Подождите 1 минуту.')
    const msg = (data.message as string | undefined) ?? 'Ошибка сервера'
    throw new Error(Array.isArray(msg) ? (msg as string[]).join('. ') : msg)
  }
  return data
}

async function callResendVerification(email: string): Promise<void> {
  const { ok, status } = await apiPost('/auth/resend-verification', { email })
  if (!ok) {
    if (status === 429) throw new Error('Слишком много попыток. Подождите 1 минуту.')
    throw new Error('Не удалось отправить код. Попробуйте позже.')
  }
}

async function callForgotPassword(email: string): Promise<void> {
  let res: Response
  try {
    res = await fetch(`${BASE}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
  } catch {
    throw new Error('Нет соединения с сервером.')
  }
  if (res.ok || res.status === 204) return
  const ct = res.headers.get('content-type') ?? ''
  if (ct.includes('application/json')) {
    const data = await res.json() as Record<string, unknown>
    const msg = data.message as string | undefined
    throw new Error(typeof msg === 'string' ? msg : 'Ошибка. Попробуйте позже.')
  }
  throw new Error(`Ошибка сервера (HTTP ${res.status}).`)
}

async function checkApiHealth(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/health`, { signal: AbortSignal.timeout(4000) })
    return res.ok
  } catch {
    return false
  }
}

export function LoginPage({ onLogin }: Props) {
  const [mode, setMode] = useState<Mode>(
    new URLSearchParams(window.location.search).get('tab') === 'register' ? 'register' : 'login'
  )
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPass, setShowPass] = useState(false)
  const [forgotSent, setForgotSent] = useState(false)
  const [apiDown, setApiDown] = useState(false)
  const [agreeToTerms, setAgreeToTerms] = useState(false)
  const [agreeToMarketing, setAgreeToMarketing] = useState(false)
  const [needsVerification, setNeedsVerification] = useState(false)
  const [resendSent, setResendSent] = useState(false)
  const [resendError, setResendError] = useState('')

  useEffect(() => {
    document.documentElement.classList.remove('dark')
    window.scrollTo(0, 0)
    document.body.style.overflow = 'auto'
    document.documentElement.style.overflow = 'auto'
    document.body.style.height = 'auto'

    checkApiHealth().then(ok => setApiDown(!ok))

    const params = new URLSearchParams(window.location.search)
    const accessToken = params.get('accessToken')
    const refreshToken = params.get('refreshToken')
    if (accessToken && refreshToken) {
      localStorage.setItem('bst-token', accessToken)
      localStorage.setItem('bst-refresh', refreshToken)
      localStorage.setItem('bst-auth', 'true')
      window.history.replaceState({}, '', window.location.pathname)
      onLogin()
    }
    if (params.get('hint') === 'alreadyVerified') {
      setError('Email уже подтверждён. Войдите с паролем.')
      window.history.replaceState({}, '', window.location.pathname)
    }

    return () => {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
      document.body.style.height = ''
    }
  }, [onLogin])

  function switchMode(m: Mode) {
    setMode(m)
    setError('')
    setPassword('')
    setForgotSent(false)
    setNeedsVerification(false)
    setResendSent(false)
    setResendError('')
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim() || !password.trim()) {
      setError('Заполните email и пароль')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Введите корректный email')
      return
    }
    if (mode === 'register' && password.length < 8) {
      setError('Пароль должен содержать минимум 8 символов')
      return
    }
    if (mode === 'register' && !agreeToTerms) {
      setError('Необходимо принять условия оферты')
      return
    }
    setLoading(true)
    setError('')
    setNeedsVerification(false)

    try {
      if (mode === 'login') {
        const data = await callLogin(email.trim(), password)
        localStorage.setItem('bst-token', data.accessToken as string)
        if (data.refreshToken) localStorage.setItem('bst-refresh', data.refreshToken as string)
        localStorage.setItem('bst-auth', 'true')
        onLogin()
      } else {
        const body: Record<string, unknown> = {
          email: email.trim(),
          password,
          agreeToTerms,
          agreeToMarketing,
        }
        if (name.trim()) body.name = name.trim()
        const ref = new URLSearchParams(window.location.search).get('ref')
        if (ref) body.referralCode = ref

        const result = await callRegister(body)
        if ((result as { needsVerification?: boolean }).needsVerification) {
          window.location.href = `/verify-email?email=${encodeURIComponent(email.trim())}`
        } else if ((result as Record<string, unknown>).accessToken) {
          // Legacy: immediate login (shouldn't happen with new flow)
          localStorage.setItem('bst-token', (result as Record<string, unknown>).accessToken as string)
          if ((result as Record<string, unknown>).refreshToken) {
            localStorage.setItem('bst-refresh', (result as Record<string, unknown>).refreshToken as string)
          }
          localStorage.setItem('bst-auth', 'true')
          onLogin()
        }
      }
    } catch (err) {
      const msg = (err as Error).message
      if (msg === 'NEEDS_VERIFICATION') {
        setNeedsVerification(true)
        setError('')
      } else {
        setError(msg)
      }
    } finally {
      setLoading(false)
    }
  }

  async function handleForgotSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) { setError('Введите email'); return }
    setLoading(true)
    setError('')
    try {
      await callForgotPassword(email.trim())
      setForgotSent(true)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  async function handleResend() {
    setResendSent(false)
    setResendError('')
    try {
      await callResendVerification(email.trim())
      setResendSent(true)
    } catch (err) {
      setResendError((err as Error).message)
    }
  }

  const inputStyle = (hasError: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '11px 14px',
    background: '#f5f5f5',
    border: `1px solid ${hasError ? '#ef4444' : '#e0e0e0'}`,
    borderRadius: '10px',
    color: '#1a1a1a',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  })

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f5f5',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    }}>
      {apiDown && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: '#dc2626', color: '#fff', padding: '10px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: '13px', fontWeight: 600,
        }}>
          <span>
            ⚠ API сервер недоступен.{' '}
            {isLocalDev
              ? <>Запустите: <code style={{ background: 'rgba(0,0,0,0.2)', padding: '2px 6px', borderRadius: '4px' }}>pnpm dev</code></>
              : 'Сервер временно недоступен. Попробуйте позже.'}
          </span>
          <button onClick={() => checkApiHealth().then(ok => setApiDown(!ok))} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
            Проверить снова
          </button>
        </div>
      )}

      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
        <div style={{
          position: 'absolute', top: '30%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px', height: '400px',
          background: 'radial-gradient(ellipse, rgba(91,106,240,0.08) 0%, transparent 70%)',
          borderRadius: '50%',
        }} />
      </div>

      <div style={{
        position: 'relative', zIndex: 1,
        width: '100%', maxWidth: '400px',
        background: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: '20px',
        padding: '40px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '10px',
              background: '#5b6af0',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 900, color: '#fff', fontSize: '16px',
              boxShadow: '0 4px 16px rgba(91,106,240,0.25)',
            }}>B</div>
            <span style={{ fontWeight: 900, fontSize: '24px', color: '#1a1a1a', letterSpacing: '-0.5px' }}>BST</span>
            <span style={{
              fontSize: '10px', fontWeight: 700,
              background: 'rgba(91,106,240,0.1)', color: '#5b6af0',
              border: '1px solid rgba(91,106,240,0.25)',
              padding: '2px 7px', borderRadius: '20px',
            }}>beta</span>
          </div>
          <div style={{ fontSize: '14px', color: '#9b9b9b', marginTop: '4px' }}>
            {mode === 'login' && 'Вход в личный кабинет'}
            {mode === 'register' && 'Создать аккаунт'}
            {mode === 'forgot' && 'Восстановление пароля'}
          </div>
        </div>

        {/* Tabs */}
        {mode !== 'forgot' && (
          <div style={{ display: 'flex', background: '#f5f5f5', borderRadius: '10px', padding: '3px', marginBottom: '24px' }}>
            {(['login', 'register'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => switchMode(m)}
                style={{
                  flex: 1, padding: '8px', border: 'none', borderRadius: '8px', cursor: 'pointer',
                  fontSize: '13px', fontWeight: 600, transition: 'all 0.15s',
                  background: mode === m ? '#fff' : 'transparent',
                  color: mode === m ? '#1a1a1a' : '#9b9b9b',
                  boxShadow: mode === m ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                {m === 'login' ? 'Войти' : 'Регистрация'}
              </button>
            ))}
          </div>
        )}

        {/* ── Форма «Забыли пароль?» ─────────────────────────────────────── */}
        {mode === 'forgot' && !forgotSent && (
          <form onSubmit={handleForgotSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#666', lineHeight: 1.6 }}>
              Введите email — мы отправим ссылку для сброса пароля. Также подходит, если вы регистрировались через GitHub и хотите задать пароль.
            </p>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#6b6b6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError('') }}
                placeholder="you@example.com"
                autoComplete="email"
                autoFocus
                style={inputStyle(!!error)}
                onFocus={(e) => { e.target.style.borderColor = '#5b6af0' }}
                onBlur={(e) => { e.target.style.borderColor = error ? '#ef4444' : '#e0e0e0' }}
              />
            </div>
            {error && (
              <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', fontSize: '13px', color: '#dc2626' }}>
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: '4px', padding: '13px',
                background: loading ? '#3a4ab0' : '#5b6af0',
                color: '#fff', border: 'none', borderRadius: '10px',
                fontSize: '14px', fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 20px rgba(91,106,240,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}
            >
              {loading ? (
                <><span style={{ display: 'inline-block', width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />Отправка...</>
              ) : 'Отправить ссылку'}
            </button>
            <button type="button" onClick={() => switchMode('login')} style={{ background: 'none', border: 'none', color: '#5b6af0', fontSize: '13px', fontWeight: 600, cursor: 'pointer', padding: '4px 0' }}>
              ← Назад ко входу
            </button>
          </form>
        )}

        {/* ── Успех «Забыли пароль?» ─────────────────────────────────────── */}
        {mode === 'forgot' && forgotSent && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '4px' }}>📬</div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#1a1a1a' }}>Письмо отправлено!</h3>
            <p style={{ margin: 0, fontSize: '14px', color: '#666', lineHeight: 1.6 }}>
              Мы отправили ссылку для сброса пароля на <strong>{email}</strong>.<br />
              Проверьте папку «Спам», если письмо не пришло в течение минуты.
            </p>
            <button type="button" onClick={() => switchMode('login')} style={{ marginTop: '8px', padding: '12px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 20px rgba(91,106,240,0.25)' }}>
              Войти в аккаунт
            </button>
          </div>
        )}

        {/* ── Основная форма (login / register) ─────────────────────────── */}
        {mode !== 'forgot' && (
          <>
            {/* Уведомление: email не подтверждён */}
            {needsVerification && (
              <div style={{
                padding: '14px 16px',
                background: 'rgba(245,158,11,0.08)',
                border: '1px solid rgba(245,158,11,0.3)',
                borderRadius: '10px',
                marginBottom: '16px',
                fontSize: '13px',
                color: '#92400e',
                lineHeight: 1.6,
              }}>
                <strong>Подтвердите email</strong><br />
                Мы отправили 6-значный код на <strong>{email}</strong>.
                <div style={{ marginTop: '10px' }}>
                  <a
                    href={`/verify-email?email=${encodeURIComponent(email.trim())}`}
                    style={{ display: 'inline-block', padding: '7px 14px', background: '#f59e0b', color: '#fff', borderRadius: '7px', fontWeight: 700, fontSize: '13px', textDecoration: 'none' }}
                  >
                    Ввести код →
                  </a>
                </div>
                {resendSent
                  ? <div style={{ marginTop: '8px', color: '#059669', fontWeight: 600 }}>✓ Новый код отправлен</div>
                  : resendError
                    ? <div style={{ marginTop: '8px', color: '#dc2626', fontSize: '12px' }}>{resendError}</div>
                    : (
                      <div style={{ marginTop: '8px' }}>
                        <button
                          type="button"
                          onClick={handleResend}
                          style={{ background: 'none', border: 'none', color: '#5b6af0', fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: '13px' }}
                        >
                          Отправить код повторно →
                        </button>
                      </div>
                    )
                }
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {mode === 'register' && (
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#6b6b6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Имя (необязательно)</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ваше имя"
                    autoComplete="name"
                    style={inputStyle(false)}
                    onFocus={(e) => { e.target.style.borderColor = '#5b6af0' }}
                    onBlur={(e) => { e.target.style.borderColor = '#e0e0e0' }}
                  />
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#6b6b6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); setNeedsVerification(false) }}
                  placeholder="you@example.com"
                  autoComplete="email"
                  autoFocus
                  style={inputStyle(!!error)}
                  onFocus={(e) => { e.target.style.borderColor = '#5b6af0' }}
                  onBlur={(e) => { e.target.style.borderColor = error ? '#ef4444' : '#e0e0e0' }}
                />
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#6b6b6b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Пароль {mode === 'register' && <span style={{ fontWeight: 400, textTransform: 'none' }}>(мин. 8 символов)</span>}
                  </label>
                  {mode === 'login' && (
                    <button type="button" tabIndex={-1} onClick={() => switchMode('forgot')} style={{ background: 'none', border: 'none', color: '#5b6af0', fontSize: '12px', fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                      Забыли пароль?
                    </button>
                  )}
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError('') }}
                    placeholder={mode === 'register' ? 'Придумайте пароль' : 'Введите пароль'}
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    style={{ ...inputStyle(!!error), paddingRight: '40px' }}
                    onFocus={(e) => { e.target.style.borderColor = '#5b6af0' }}
                    onBlur={(e) => { e.target.style.borderColor = error ? '#ef4444' : '#e0e0e0' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9b9b9b', fontSize: '14px', padding: '2px' }}
                  >
                    {showPass ? '🙈' : '👁'}
                  </button>
                </div>
              </div>

              {/* Галочки при регистрации */}
              {mode === 'register' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '2px' }}>
                  <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={agreeToTerms}
                      onChange={(e) => { setAgreeToTerms(e.target.checked); setError('') }}
                      style={{ marginTop: '3px', flexShrink: 0, accentColor: '#5b6af0', width: '15px', height: '15px' }}
                    />
                    <span style={{ fontSize: '12px', color: '#555', lineHeight: 1.5 }}>
                      Я принимаю{' '}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#5b6af0', textDecoration: 'none', fontWeight: 600 }}>условия оферты</a>
                      {' '}и{' '}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#5b6af0', textDecoration: 'none', fontWeight: 600 }}>политику конфиденциальности</a>
                      {' '}<span style={{ color: '#ef4444' }}>*</span>
                    </span>
                  </label>
                  <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={agreeToMarketing}
                      onChange={(e) => setAgreeToMarketing(e.target.checked)}
                      style={{ marginTop: '3px', flexShrink: 0, accentColor: '#5b6af0', width: '15px', height: '15px' }}
                    />
                    <span style={{ fontSize: '12px', color: '#555', lineHeight: 1.5 }}>
                      Я согласен получать новости и специальные предложения BST
                    </span>
                  </label>
                </div>
              )}

              {error && (
                <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', fontSize: '13px', color: '#dc2626' }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  marginTop: '4px', padding: '13px',
                  background: loading ? '#3a4ab0' : '#5b6af0',
                  color: '#fff', border: 'none', borderRadius: '10px',
                  fontSize: '14px', fontWeight: 700,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 20px rgba(91,106,240,0.25)',
                  transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                }}
              >
                {loading ? (
                  <><span style={{ display: 'inline-block', width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />{mode === 'login' ? 'Вход...' : 'Регистрация...'}</>
                ) : (mode === 'login' ? 'Войти в кабинет' : 'Создать аккаунт')}
              </button>
            </form>

            {/* OAuth buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0 0' }}>
              <div style={{ flex: 1, height: '1px', background: '#e0e0e0' }} />
              <span style={{ fontSize: '12px', color: '#9b9b9b', whiteSpace: 'nowrap' }}>или войти через</span>
              <div style={{ flex: 1, height: '1px', background: '#e0e0e0' }} />
            </div>

            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* Google */}
              <button
                type="button"
                onClick={() => { window.location.href = '/api/auth/google' }}
                style={{
                  width: '100%', padding: '11px 14px',
                  background: '#fff', color: '#333',
                  border: '1px solid #e0e0e0', borderRadius: '10px',
                  fontSize: '14px', fontWeight: 600,
                  cursor: 'pointer', transition: 'box-shadow 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)' }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Продолжить через Google
              </button>

              {/* GitHub */}
              <button
                type="button"
                onClick={() => { window.location.href = '/api/auth/github' }}
                style={{
                  width: '100%', padding: '11px 14px',
                  background: '#1a1a1a', color: '#fff',
                  border: '1px solid #1a1a1a', borderRadius: '10px',
                  fontSize: '14px', fontWeight: 600,
                  cursor: 'pointer', transition: 'opacity 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85' }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
                </svg>
                Продолжить через GitHub
              </button>
            </div>
          </>
        )}
      </div>

      <div style={{ position: 'relative', zIndex: 1, marginTop: '24px', fontSize: '12px', color: '#9b9b9b', textAlign: 'center' }}>
        © {new Date().getFullYear()} BST ·{' '}
        <a href="/terms" style={{ color: '#9b9b9b', textDecoration: 'none' }}>Оферта</a>
        {' · '}
        <a href="/privacy" style={{ color: '#9b9b9b', textDecoration: 'none' }}>Конфиденциальность</a>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: #9b9b9b; }
      `}</style>
    </div>
  )
}
